import { ModalActions, ModalActionType } from './actions';
import { Modal, MODAL_COMPONENTS, MODALS_ORDER } from '@/features/modal/types';

export interface ModalState {
  // Legacy state
  showBalanceUpdate: boolean;
  showUserIsPepHioLogout: boolean;
  showFtdExitIntent: boolean;
  // New modal system state
  isOpen: boolean;
  modals: Modal[];
}

const initialState: ModalState = {
  // Legacy state
  showBalanceUpdate: false,
  showUserIsPepHioLogout: false,
  showFtdExitIntent: false,
  // New modal system state
  isOpen: false,
  modals: [],
};

// Helper to sort modals by priority order.
// Earlier in MODALS_ORDER = higher priority = shown first (placed last in the state array,
// since ModalContainer renders modals[modals.length - 1]).
// Not used on open — new modals are always pushed to the end (displayed on top).
// Used on close to reorder remaining modals so the highest-priority one is displayed next.
const sortModals = (a: Modal, b: Modal): number => {
  const indexOfA = MODALS_ORDER.indexOf(a.name);
  const indexOfB = MODALS_ORDER.indexOf(b.name);

  // Both in priority list: sort by priority (earlier in MODALS_ORDER = higher priority = later in state array)
  if (indexOfA !== -1 && indexOfB !== -1) {
    return indexOfB - indexOfA;
  }

  // Non-priority modals sort before priority ones (priority modals stay at end = displayed on top)
  if (indexOfA !== -1) return 1;
  if (indexOfB !== -1) return -1;

  // Both non-priority: maintain insertion order (stable sort)
  return 0;
};

export const modalReducer = (state = initialState, action: ModalActions): ModalState => {
  switch (action.type) {
    // Legacy actions
    case ModalActionType.ShowBalanceUpdate:
      return {
        ...state,
        showBalanceUpdate: action.isShown,
      };

    case ModalActionType.ShowUserIsPepOrHioLogout:
      return {
        ...state,
        showUserIsPepHioLogout: action.isShown,
      };

    case ModalActionType.SetFtdExitIntentShown:
      return {
        ...state,
        showFtdExitIntent: true,
      };

    // New modal system actions
    case ModalActionType.ModalOpen: {
      const { modal, props } = action;
      const modals = [...state.modals];
      const targetIndex = modals.findIndex(currentModal => currentModal.name === modal);

      if (targetIndex === -1) {
        // New modal: push to end so it's displayed on top immediately
        modals.push({
          name: modal,
          props: props ?? {},
          component: MODAL_COMPONENTS[modal],
        });
        return {
          ...state,
          modals,
          isOpen: true,
        };
      }

      // Existing modal: update props and move to end so it's displayed on top
      const target = modals[targetIndex];
      modals.splice(targetIndex, 1);
      modals.push({
        ...target,
        props: { ...target.props, ...props },
      });

      return {
        ...state,
        modals,
        isOpen: true,
      };
    }

    case ModalActionType.ModalClose: {
      const { modal } = action;
      const modals = [...state.modals];
      const hasModal = modal !== undefined;
      const targetIndex = hasModal
        ? modals.findIndex(currentModal => currentModal.name === modal)
        : -1;

      if (targetIndex === -1 && hasModal) {
        return state;
      }

      // Check whether the modal being removed is the top-most (currently displayed) one
      const isTopModal = hasModal ? targetIndex === modals.length - 1 : true;

      if (targetIndex !== -1 && hasModal) {
        modals.splice(targetIndex, 1);
      } else {
        modals.pop();
      }

      // Only re-sort when the top-most modal was closed, so the next highest-priority
      // modal is displayed. Closing a non-top modal shouldn't change what's currently shown.
      if (isTopModal) {
        modals.sort(sortModals);
      }

      return {
        ...state,
        modals,
        isOpen: modals.length > 0,
      };
    }

    case ModalActionType.ModalReset:
      return {
        ...state,
        isOpen: false,
        modals: [],
      };

    case ModalActionType.ModalResetExcluded: {
      const { excludedModals } = action;
      const filteredModals = state.modals.filter(modal => excludedModals.includes(modal.name));
      return {
        ...state,
        modals: filteredModals,
        isOpen: filteredModals.length > 0,
      };
    }

    case ModalActionType.ModalCloseAll: {
      const { exceptions } = action;
      if (!exceptions || exceptions.length === 0) {
        return {
          ...state,
          modals: [],
          isOpen: false,
        };
      }
      const filteredModals = state.modals.filter(modal => exceptions.includes(modal.name));
      return {
        ...state,
        modals: filteredModals,
        isOpen: filteredModals.length > 0,
      };
    }

    default:
      return state;
  }
};
